import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Search, UserPlus, Calendar } from "lucide-react";
import { toast } from "sonner";
import AppointmentSlotPicker, { slotToScheduledAt } from "../components/AppointmentSlotPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const GENDER_OPTIONS = ["Male", "Female", "Other"];

const empty = {
  name: "",
  unique_id: "",
  age: "",
  gender: "",
  phone: "",
  email: "",
  address: "",
  medical_history: "",
  notes: "",
};

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [bookAppt, setBookAppt] = useState(false);
  const [apptData, setApptData] = useState({
    date: new Date().toISOString().slice(0, 10),
    hhmm: "",
    consultation_type: "General consultation",
    fees: 250,
    is_walkin: false,
  });
  const PAGE = 24;

  const load = useCallback((search = "", size = PAGE) => {
    api
      .get("/patients", { params: { ...(search ? { q: search } : {}), limit: size } })
      .then((r) => {
        setPatients(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch((err) => console.error("Failed to load patients", err));
  }, []);

  const openNew = async () => {
    setForm(empty);
    setOpen(true);
    try {
      const r = await api.get("/patients/next-id");
      setForm((f) => ({ ...f, unique_id: r.data.unique_id }));
    } catch (err) {
      /* silent — user can enter manually */
      console.warn("next-id lookup failed", err);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (bookAppt && !apptData.hhmm) {
      toast.error("Pick an appointment time slot");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, age: form.age ? parseInt(form.age) : null };
      const patientRes = await api.post("/patients", payload);
      const patient = patientRes.data;
      if (bookAppt) {
        const scheduled_at = slotToScheduledAt(apptData);
        await api.post("/appointments", {
          patient_id: patient.id,
          scheduled_at,
          consultation_type: apptData.consultation_type,
          fees: apptData.fees,
          is_walkin: apptData.is_walkin,
          status: "scheduled",
        });
      }
      toast.success(bookAppt ? "Patient added & appointment booked" : "Patient added");
      setForm(empty);
      setBookAppt(false);
      setApptData({
        date: new Date().toISOString().slice(0, 10),
        hhmm: "",
        consultation_type: "General consultation",
        fees: 250,
        is_walkin: false,
      });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add patient");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-secondary font-bold">
            Care Records
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Patients</h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Browse, search and add patients to your wellness hub.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-patient-btn" size="lg" onClick={openNew}>
              <UserPlus className="size-4 mr-2" /> Add Patient
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[92vh] p-0 gap-0 flex flex-col">
            <DialogHeader className="px-4 sm:px-6 pt-5 sm:pt-6 pb-3 shrink-0">
              <DialogTitle className="font-heading">New patient</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={handleCreate}
              className="flex flex-col min-h-0 flex-1"
              data-testid="patient-form"
            >
              <div className="px-4 sm:px-6 pb-4 space-y-3 overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Name *</Label>
                  <Input
                    data-testid="patient-form-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Unique ID *</Label>
                  <Input
                    data-testid="patient-form-uniqueid"
                    required
                    placeholder="NM-005"
                    value={form.unique_id}
                    onChange={(e) => setForm({ ...form, unique_id: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Age</Label>
                  <Input
                    type="number"
                    value={form.age}
                    onChange={(e) => setForm({ ...form, age: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select
                    value={form.gender || ""}
                    onValueChange={(v) => setForm({ ...form, gender: v })}
                  >
                    <SelectTrigger data-testid="patient-form-gender" className="w-full">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map((g) => (
                        <SelectItem key={g} value={g}>
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>
              <div>
                <Label>Medical history</Label>
                <Textarea
                  rows={3}
                  value={form.medical_history}
                  onChange={(e) => setForm({ ...form, medical_history: e.target.value })}
                />
              </div>

              <div className="border-t border-border pt-4 space-y-3">
                <label
                  htmlFor="book-appt-checkbox"
                  className="flex items-center gap-3 cursor-pointer select-none"
                  data-testid="toggle-book-appt"
                >
                  <input
                    id="book-appt-checkbox"
                    type="checkbox"
                    className="size-4 accent-primary cursor-pointer"
                    checked={bookAppt}
                    onChange={(e) => setBookAppt(e.target.checked)}
                  />
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="size-4 text-primary" />
                    Also book first appointment
                  </span>
                </label>
                {bookAppt && (
                  <div className="rounded-md border border-border bg-muted/30 p-4">
                    <AppointmentSlotPicker
                      value={apptData}
                      onChange={setApptData}
                      defaultDate={apptData.date}
                    />
                  </div>
                )}
              </div>
              </div>

              <DialogFooter className="px-4 sm:px-6 py-3 border-t border-border bg-white shrink-0">
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="patient-form-submit"
                >
                  {saving ? "Saving…" : bookAppt ? "Save & book" : "Save patient"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <div className="relative">
        <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="patients-search-input"
          placeholder="Search by name, unique ID or phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 h-11"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {patients.map((p) => (
          <Link
            key={p.id}
            to={`/patients/${p.id}`}
            data-testid={`patient-link-${p.unique_id}`}
          >
            <Card className="p-5 h-full hover:border-primary/40 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-heading font-semibold text-lg">{p.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {p.age ? `${p.age} yrs` : ""} {p.gender ? `• ${p.gender}` : ""}
                  </p>
                </div>
                <Badge variant="outline">{p.unique_id}</Badge>
              </div>
              {p.phone && (
                <p className="text-xs text-muted-foreground">{p.phone}</p>
              )}
              {p.medical_history && (
                <p className="text-sm text-foreground/80 line-clamp-3 mt-3">
                  {p.medical_history}
                </p>
              )}
            </Card>
          </Link>
        ))}
        {patients.length === 0 && (
          <Card className="col-span-full p-10 text-center border-dashed">
            <p className="text-muted-foreground">No patients yet. Add your first one.</p>
          </Card>
        )}
      </div>

      {patients.length < total && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => load(q, patients.length + PAGE)}
            data-testid="load-more-patients"
          >
            Load more ({total - patients.length} remaining)
          </Button>
        </div>
      )}
    </div>
  );
}
